import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_ADMIN_API_KEYS_LIST,
  API_ADMIN_API_KEYS_CREATE,
  API_ADMIN_API_KEYS_REVOKE,
  API_ADMIN_API_KEYS_USAGE,
  API_ADMIN_API_KEYS_ROTATE,
  API_ADMIN_API_KEYS_API_USERS,
  API_ADMIN_API_KEYS_SET_LINKED_USER,
  API_ADMIN_API_KEYS_SET_TENANT,
  API_ADMIN_API_KEYS_TEST,
} from '../../constants/apiConstants';

export interface ApiKeyDto {
  id: number;
  apiKeyName: string;
  apiKeyPrefix: string;
  description: string | null;
  expiresAt: string | null;
  isActive: boolean;
  rateLimitPerMinute: number | null;
  allowedScopes: string | null;
  allowedIpAddresses: string | null;
  lastUsedAt: string | null;
  usageCount: number;
  createdByUserId: number;
  createdAt: string;
  linkedUserId: number | null;
  linkedUserName: string | null;
  tenantId: number | null;
  tenantName: string | null;
}

export interface ApiUserDto {
  id: number;
  userName: string;
  displayName: string | null;
}

export interface CreateApiKeyRequest {
  name: string;
  description: string | null;
  expiresAt: string | null;
  rateLimitPerMinute: number | null;
  allowedScopes: string | null;
  allowedIpAddresses: string | null;
  linkedUserId: number | null;
  tenantId?: number | null;
}

export interface CreateApiKeyResponse {
  apiKey: string;
  apiKeyId: number;
  apiKeyPrefix: string;
  message: string;
}

export interface DailyUsageDto {
  date: string;
  usageCount: number;
}

export interface ApiKeyUsageStats {
  apiKeyId: number;
  apiKeyName: string;
  totalUsageCount: number;
  lastUsedAt: string | null;
  firstUsedAt: string | null;
  usageInPeriod: number;
  dailyUsage: DailyUsageDto[];
}

export interface ApiKeyListResponse {
  apiKeys: ApiKeyDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface RotateApiKeyResponse {
  apiKey: string;
  apiKeyId: number;
  apiKeyPrefix: string;
  message: string;
}

export interface ApiKeyTestResult {
  isValid: boolean;
  apiKeyName?: string;
  apiKeyPrefix?: string;
  allowedScopes?: string;
  rateLimitPerMinute?: number;
  tenantId?: number;
  tenantCode?: string;
  linkedUserId?: number;
  errorMessage?: string;
  hasScopeAccess?: boolean;
}

export const apiKeyService = {
  async setTenant(id: number, tenantId: number | null): Promise<void> {
    try {
      const response = await apiClient.patch(API_ADMIN_API_KEYS_SET_TENANT(id), { tenantId });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
    } catch (error) {
      logger.error('Failed to set tenant', error, { id });
      throw error;
    }
  },

  async setLinkedUser(id: number, linkedUserId: number | null): Promise<void> {    try {
      const response = await apiClient.patch(API_ADMIN_API_KEYS_SET_LINKED_USER(id), { linkedUserId });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
    } catch (error) {
      logger.error('Failed to set linked user', error, { id });
      throw error;
    }
  },

  async getApiUsers(): Promise<ApiUserDto[]> {
    try {
      const response = await apiClient.get(API_ADMIN_API_KEYS_API_USERS);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.users ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch API users', error);
      toast.error(`Failed to load API users: ${msg}`);
      throw error;
    }
  },

  async getApiKeys(page: number, pageSize: number): Promise<ApiKeyListResponse> {
    try {
      const response = await apiClient.get(API_ADMIN_API_KEYS_LIST, { params: { page, pageSize } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch API keys', error);
      toast.error(`Failed to load API keys: ${msg}`);
      throw error;
    }
  },

  async createApiKey(request: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    try {
      const response = await apiClient.post(API_ADMIN_API_KEYS_CREATE, request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('API key created successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to create API key', error);
      toast.error(`Failed to create API key: ${msg}`);
      throw error;
    }
  },

  async revokeApiKey(id: number): Promise<void> {
    try {
      const response = await apiClient.delete(API_ADMIN_API_KEYS_REVOKE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('API key revoked successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to revoke API key', error, { id });
      toast.error(`Failed to revoke API key: ${msg}`);
      throw error;
    }
  },

  async rotateApiKey(id: number): Promise<RotateApiKeyResponse> {
    try {
      const response = await apiClient.post(API_ADMIN_API_KEYS_ROTATE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('API key rotated successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to rotate API key', error, { id });
      toast.error(`Failed to rotate API key: ${msg}`);
      throw error;
    }
  },

  async getApiKeyUsage(id: number, fromDate?: string, toDate?: string): Promise<ApiKeyUsageStats> {
    try {
      const response = await apiClient.get(API_ADMIN_API_KEYS_USAGE(id), {
        params: { fromDate, toDate },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch API key usage', error, { id });
      toast.error(`Failed to load usage stats: ${msg}`);
      throw error;
    }
  },

  async testApiKey(apiKey: string, scope?: string): Promise<ApiKeyTestResult> {
    const response = await apiClient.post(API_ADMIN_API_KEYS_TEST, { apiKey, scope: scope || null });
    if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
    return response.data.data;
  },
};
